/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 195 lines
 * - Adding: ~6 lines
 * - Projected: 201 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts, auth pages
 */

import { FunctionComponent, useState, useEffect } from "react";
import { useFormik } from "formik";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { resetPassword } from "../../services/usersService";
import PasswordInput from "../../components/auth/PasswordInput";
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UI_STANDARDIZATION
 * Date:         2025-01-XX
 * Modified By:  Cursor
 *
 * Description:
 * Replaced direct toast.* calls with centralized notify helper for consistent messaging.
 *
 * Related Screens / Modules:
 * - client/src/pages/auth/ResetPassword.tsx
 * - client/src/utils/notifications.ts
 */

import { notify } from "../../utils/notifications";
import * as yup from "yup";

interface ResetPasswordFormValues {
  newPassword: string;
  confirmPassword: string;
}

const resetPasswordValidationSchema = yup.object({
  newPassword: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "Password at least 8 characters with uppercase, lowercase, numbers, and special characters",
    ),
  confirmPassword: yup
    .string()
    .required("Please confirm your password")
    .oneOf([yup.ref("newPassword")], "Passwords must match"),
});

const ResetPassword: FunctionComponent = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Extract token from URL query parameters
    const tokenFromUrl = searchParams.get("token");
    if (!tokenFromUrl) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(null, "This reset link is invalid or expired. Please request a new password reset");
    }
    setToken(tokenFromUrl);
  }, [searchParams]);

  const formik = useFormik<ResetPasswordFormValues>({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: resetPasswordValidationSchema,
    onSubmit: async (values) => {
      if (!token) {
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(null, "This reset link is invalid or expired. Please request a new password reset");
        return;
      }

      setIsLoading(true);
      try {
        await resetPassword(token, values.newPassword);
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.success("Your password has been updated. You can now log in");
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } catch (err: any) {
        console.error("Password reset error:", err);
        let errorMessage = "We couldn't reset your password. Please try again or contact support if the problem continues";

        if (err.response?.data) {
          if (typeof err.response.data === "string") {
            errorMessage = err.response.data;
          } else if (err.response.data.code === "INVALID_OR_EXPIRED_TOKEN") {
            errorMessage = "Invalid or expired reset link. Please request a new password reset.";
          } else if (err.response.data.code === "WEAK_PASSWORD") {
            errorMessage =
              "Password must contain at least 8 characters with uppercase, lowercase, numbers, and special characters";
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          }
        }

        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(err, errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
  });

  // If no token, show error message with link to request new reset
  if (!token) {
    return (
      <div className="container d-flex justify-content-center align-items-center auth-fullheight">
        <div className="card auth-card-max-width">
          <div className="card-body p-4">
            <h2 className="signin-title mb-3">Reset Password</h2>
            <div className="alert alert-danger" role="alert">
              Invalid reset link. Please request a new password reset email.
            </div>
            <div className="text-center mt-3">
              <Link to="/" className="btn-black">
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container d-flex justify-content-center align-items-center auth-fullheight">
      <div className="card auth-card-max-width">
        <div className="card-body p-4">
          <h2 className="signin-title mb-4">Reset Password</h2>
          <p className="text-muted mb-4">Please enter your new password below.</p>

          <form onSubmit={formik.handleSubmit}>
            <div className="mb-3">
              <label htmlFor="newPassword" className="form-label">
                New Password
              </label>
              <PasswordInput
                name="newPassword"
                placeholder="Enter new password"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showPassword={showPassword}
                onTogglePassword={() => setShowPassword(!showPassword)}
                error={formik.errors.newPassword}
                touched={formik.touched.newPassword}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password
              </label>
              <PasswordInput
                name="confirmPassword"
                placeholder="Confirm new password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showPassword={showConfirmPassword}
                onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
                error={formik.errors.confirmPassword}
                touched={formik.touched.confirmPassword}
              />
            </div>

            <button
              type="submit"
              className="btn-black w-100 h-60 text-16 fw-600"
              disabled={isLoading || !formik.isValid}
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </button>
          </form>

          <div className="text-center mt-4">
            <Link to="/" className="text-decoration-none">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
